
import { TargetModel } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Ref, Watch } from 'vue-property-decorator';
import { Guid } from '@/libs/common/functions/guid';
import { targetsStore } from '@/libs/targets/+state/store';
import Loader from '@/libs/common/components/Loader.vue';
import ConfirmDialog from '@/libs/common/components/ConfirmDialog.vue';
import WorkplaceSelect from '@/libs/common/components-smart/WorkplaceSelect.vue';
import LocationSelect from '@/libs/common/components-smart/LocationSelect.vue';
import moment from 'moment';

@Component({
    components: { Loader, ConfirmDialog, WorkplaceSelect, LocationSelect },
})
export default class TargetDialog extends Page {
    id!: string;
    dialog = false;
    valid = true;
    saving = false;
    loading = false;
    target: TargetModel | null = null;
    deleteDialog = false;
    deleting = false;
    scopeType: 'location' | 'workplace' | 'global' = 'location';

    // internal month selection state (YYYY-MM)
    monthFrom: string | null = null;
    monthTo: string | null = null;
    menuFrom = false;
    menuTo = false;

    @Ref() form!: any;

    get isNewItem(): boolean {
        return this.target?.id == Guid.EmptyGuid();
    }

    get isScopeValid(): boolean {
        const hasLocation = !!this.target?.locationId;
        const hasWorkplace = !!this.target?.workplaceId;
        if (this.scopeType === 'global') {
            return !hasLocation && !hasWorkplace;
        }
        return (hasLocation && !hasWorkplace) || (!hasLocation && hasWorkplace);
    }

    get hasAnyOee(): boolean {
        const t = this.target as any;
        return (
            (t?.worldClassOee != null && t.worldClassOee !== '') ||
            (t?.targetOeeAvailability != null && t.targetOeeAvailability !== '') ||
            (t?.targetOeePerformance != null && t.targetOeePerformance !== '') ||
            (t?.targetOeeQuality != null && t.targetOeeQuality !== '')
        );
    }

    get displayName(): string {
        const anyTarget: any = this.target as any;
        return (anyTarget?.workplace?.name as string) || (anyTarget?.location?.name as string) || '';
    }

    get fromText(): string {
        return this.monthFrom ? moment(this.monthFrom, 'YYYY-MM').format('MM/YYYY') : '';
    }

    get toText(): string {
        return this.monthTo ? moment(this.monthTo, 'YYYY-MM').format('MM/YYYY') : '';
    }

    get computedTargetOee(): string {
        if (!this.target) return '-';
        const a = this.target.targetOeeAvailability;
        const e = this.target.targetOeePerformance;
        const q = this.target.targetOeeQuality;
        
        // Všechny tři složky musí být vyplněny pro výpočet OEE
        if (a != null && e != null && q != null) {
            const oee = (Number(a) * Number(e) * Number(q)) / 10000;
            return oee.toFixed(2);
        }
        return '-';
    }

    @Watch('scopeType')
    onScopeTypeChanged(v: 'location' | 'workplace' | 'global') {
        if (!this.target) return;
        if (v === 'location') {
            this.target.workplaceId = undefined as any;
        } else if (v === 'workplace') {
            this.target.locationId = undefined as any;
        } else if (v === 'global') {
            this.target.locationId = undefined as any;
            this.target.workplaceId = undefined as any;
        }
    }

    open(id: string) {
        this.dialog = true;
        this.id = id;
        this.load();
    }

    close() {
        this.dialog = false;
    }

    deleteClick() {
        this.deleteDialog = true;
    }

    deleteTarget() {
        this.deleting = true;
        targetsStore.dispatch(CrudAction.Delete, { id: this.id });
        this.subscribe(targetsStore, CrudReponse.Delete)
            .then(() => {
                this.deleting = false;
                this.deleteDialog = false;
                this.close();
            })
            .catch(() => {
                this.deleting = false;
            });
    }

    private load(): Promise<any> {
        if (this.id != Guid.EmptyGuid()) {
            this.loading = true;
            return new Promise((resolve, reject) => {
                targetsStore.dispatch(CrudAction.Get, { id: this.id });
                this.subscribe(targetsStore, CrudReponse.Get)
                    .then((item: TargetModel) => {
                        this.target = item;
                        if (this.target.workplaceId) {
                            this.scopeType = 'workplace';
                        } else if (this.target.locationId) {
                            this.scopeType = 'location';
                        } else {
                            this.scopeType = 'global';
                        }
                        // init internal month fields from existing dates
                        this.monthFrom = this.target.fromDate ? moment(this.target.fromDate).format('YYYY-MM') : null;
                        this.monthTo = this.target.toDate ? moment(this.target.toDate).format('YYYY-MM') : null;
                        this.loading = false;
                        resolve(item);
                    })
                    .catch((e: any) => {
                        this.loading = false;
                        reject(e);
                    });
            });
        } else {
            this.target = {
                id: Guid.EmptyGuid(),
                locationId: undefined,
                workplaceId: undefined,
                worldClassOee: undefined,
                targetOee: undefined,
                targetOeeAvailability: undefined,
                targetOeePerformance: undefined,
                targetOeeQuality: undefined,
                fromDate: undefined,
                toDate: undefined,
            } as TargetModel;
            this.scopeType = 'location';
            this.monthFrom = null;
            this.monthTo = null;
            return Promise.resolve(null);
        }
    }

    save() {
        if (!this.valid || !this.target || !this.isScopeValid || !this.hasAnyOee) return;
        // enforce exclusivity before sending
        if (this.scopeType === 'location') this.target.workplaceId = undefined as any;
        if (this.scopeType === 'workplace') this.target.locationId = undefined as any;
        if (this.scopeType === 'global') { this.target.locationId = undefined as any; this.target.workplaceId = undefined as any; }

        // compute real dates from month selection
        if (this.monthFrom) {
            this.target.fromDate = moment(this.monthFrom, 'YYYY-MM').startOf('month').toISOString();
        } else {
            this.target.fromDate = undefined as any;
        }
        if (this.monthTo) {
            this.target.toDate = moment(this.monthTo, 'YYYY-MM').endOf('month').toISOString();
        } else {
            this.target.toDate = undefined as any;
        }

        // Vypočítat targetOee ze složek
        const a = this.target.targetOeeAvailability;
        const e = this.target.targetOeePerformance;
        const q = this.target.targetOeeQuality;
        if (a != null && e != null && q != null) {
            this.target.targetOee = Number(((Number(a) * Number(e) * Number(q)) / 10000).toFixed(2));
        } else {
            this.target.targetOee = undefined as any;
        }

        this.saving = true;
        const action = this.isNewItem ? CrudAction.Create : CrudAction.Update;
        targetsStore.dispatch(action, { item: this.target });
        this.subscribe(targetsStore, this.isNewItem ? CrudReponse.Create : CrudReponse.Update)
            .then(() => {
                this.saving = false;
                this.close();
            })
            .catch(() => {
                this.saving = false;
            });
    }

    // validation for date range
    get isDateRangeValid(): boolean {
        if (!this.target) return true;
        if (!this.monthFrom || !this.monthTo) return true;
        const from = moment(this.monthFrom, 'YYYY-MM').startOf('month');
        const to = moment(this.monthTo, 'YYYY-MM').endOf('month');
        return from.isBefore(to);
    }

    get oeeRules(): any[] {
        const rangeMessage = this.$i18n.t('targets.validation.oeePercentRange');
        return [(v: any) => v === null || v === undefined || v === '' || (Number(v) >= 1 && Number(v) <= 100) || rangeMessage];
    }

    clearFrom() {
        this.monthFrom = null;
        if (this.target) this.target.fromDate = undefined as any;
    }

    clearTo() {
        this.monthTo = null;
        if (this.target) this.target.toDate = undefined as any;
    }
}
